import React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

interface AutomationPauseDialogProps {
    open: boolean;
    isPausing: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const AutomationPauseDialog: React.FC<AutomationPauseDialogProps> = ({
    open,
    isPausing,
    onClose,
    onConfirm
}) => {
    const {t} = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{sx: {minWidth: 360, maxHeight: '90vh'}}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Typography variant={'h6'} fontWeight={600} noWrap>
                        {t('Pause automation')}
                    </Typography>

                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity={'info'} sx={{mt: 1}}>
                    <Stack gap={0.5}>
                        <Typography variant={'body2'}>
                            {t('Automatic triggers stop firing immediately.')}
                        </Typography>
                        <Typography variant={'body2'}>
                            {t('Runs that are already under way are not interrupted.')}
                        </Typography>
                        {/* A live run needs a live flow; a paused one can only be tested. */}
                        <Typography variant={'body2'}>
                            {t('You can still try it with Test run.')}
                        </Typography>
                        <Typography variant={'body2'}>
                            {t('You can set it live again at any time.')}
                        </Typography>
                    </Stack>
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    variant={'contained'}
                    color={'warning'}
                    loading={isPausing}
                    onClick={onConfirm}
                >
                    {t('Pause')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default AutomationPauseDialog;
